import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AuthenticateService } from '../origami-api/services/authenticate/authenticate.service';
import { AuthInfo } from '../origami-api/services/authenticate/authenticate.model';
import { MixpanelService } from '../shared/misc/analytics/mixpanel.service';
import { OpenPanelService } from '../shared/misc/analytics/open-panel.service';
import { AppService } from '@app/app.service';
import { catchError, map, tap } from 'rxjs/operators';
import { OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { ErrorCode, LogoffType } from '@app/origami-api/constants';
import { getServerConfig } from '@app/origami-api/server-config';
import { LocalStorage } from '@app/session/local-storage';
import { Router } from '@angular/router';
import { LocalStorageKeys, LocalStorageService } from '@app/services/local-storage.service';
import { GlobalEventService, IFrameViewer } from '@app/services/global-event.service';
import { AppLoadingService } from '@app/services/app-loading.service';
import { DialogService } from '@app/services/dialogs/dialog.service';

export interface AuthServiceResult {
  hasSession: boolean;
  registrationMode: boolean;
  error: number;
  info: AuthInfo;
}

@Injectable()
export class AuthService {
  readonly strings = tiStrings.app.misc;
  public authInfo: AuthServiceResult = null;
  public hasActiveViewers: boolean = false;
  public isLoggingOut: boolean = false;

  readonly logoutTime: number = 10; // seconds

  constructor(
    private readonly authenticateService: AuthenticateService,
    private readonly mixpanel: MixpanelService,
    private readonly openPanel: OpenPanelService,
    private readonly appService: AppService,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
    private readonly globalEventService: GlobalEventService,
    private readonly loadingService: AppLoadingService,
    private readonly dialogService: DialogService,
  ) {}

  login(
    options: { userName: string; password: string; domain?: string },
    silentErrorCodes?: ErrorCode[],
  ): Observable<AuthServiceResult> {
    return this.authenticateService.authenticate(options, silentErrorCodes).pipe(
      map((res) => this.prepareAuthInfo(res)),
      tap((res) => this.updateServerConfig(res)),
      tap((res) => (this.authInfo = res)),
    );
  }

  logout(logOffType?: LogoffType, profileLogout?: boolean): Promise<any> {
    this.hasActiveViewers && this.globalEventService.sendMessage(IFrameViewer.Events.destroyAllViewers, [1]);
    this.appService.removeBeforeUnloadListener();
    this.isLoggingOut = true;
    this.localStorageService.setItem(
      LocalStorageKeys.ti_session_expired,
      logOffType === LogoffType.LogOffByIdle ? 1 : 0,
    );
    return new Promise((resolve) => {
      const timeAmount: number = this.hasActiveViewers && profileLogout ? this.logoutTime * 1000 : 0;
      if (timeAmount > 0) {
        this.dialogService.showTimerModal(this.strings.saveChanges, this.strings.loggingOut, [], this.logoutTime);
        //this.loadingService.showViewportLoading();
      }
      setTimeout(() => {
        this.authenticateService
          .logoff(logOffType)
          .pipe(
            catchError((err) => {
              // console.warn(err);

              return of(err);
            }),
          )
          .subscribe((response) => {
            this.authInfo = null;
            this.mixpanel.userLogout();
            this.openPanel.userLogout();
            //this.isLoggingOut = false;

            resolve(this.authInfo);

            if (response) {
              // CAS redirect will cause to the null response
              this.appService.removeBeforeUnloadListener();
              window.sessionStorage.clear();
              window.location.reload();
            }
          });
      }, timeAmount);
    });
  }

  checkSession(force: boolean = false): Observable<AuthServiceResult> {
    if (this.authInfo && !force) {
      return of(this.authInfo);
    }
    return this.authenticateService.getAuthInfo([ErrorCode.TEMPORARY_LOGIN]).pipe(
      map((res) => this.prepareAuthInfo(res)),
      tap((res) => this.sessionErrorHandle(res.error)),
      tap((res) => this.updateServerConfig(res)),
      tap((res) => {
        LocalStorage.set('IsFirstLogin', `${res.info.IsFirstLogin}`);
        this.authInfo = res;
      }),
      catchError((err, caugth) => {
        return of({
          hasSession: false,
          registrationMode: false,
          error: err?.ErrorCode,
          info: null,
        });
      }),
    );
  }

  private sessionErrorHandle(error) {
    // TODO: do refactor for logic and reuse in login component
    const isResetPassCode = location.href.indexOf('resetPassCode') !== -1;
    if (error === ErrorCode.TEMPORARY_LOGIN && !isResetPassCode) {
      setTimeout(() => this.router.navigate(['/auth/password-reset'], { queryParams: { temporaryLogin: true } }));
    }

    if (error === ErrorCode.INVALID_SSO_USER && !isResetPassCode) {
      setTimeout(() => this.router.navigate(['/authentication-issue']));
    }
  }

  private prepareAuthInfo(response: OrigamiResponse<AuthInfo> = <OrigamiResponse<AuthInfo>>{}): AuthServiceResult {
    return {
      hasSession: !response.ErrorCode && !!response.Result && !!response.Result.SessionId,
      registrationMode: !!(response.Result && response.Result.RegistrationMode),
      error: response.ErrorCode,
      info: response.Result,
    };
  }

  private updateServerConfig = (res: AuthServiceResult) => {
    // in case of manual domain entering, the getSettingsScript will not return default domain name at all
    if (!getServerConfig().domain) {
      getServerConfig().domain = res.info.DomainName;
    }
  };
}
